import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDayMonth } from '@/shared/lib/seasonName';
import { X, Play, Loader2, Volume2, Inbox, CheckCircle2 } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const RESPONSE_BUCKET = 'help_responses';

interface ResponseRow {
  id: string;
  created_at: string;
  response_audio_path: string;
  response_heard: boolean;
}

/**
 * DISPLAY-ONLY notifications panel: lists this farmer's seeded voice responses
 * (help_messages rows with a response_audio_path), newest first. Playing one
 * streams the audio from the private help_responses bucket via a signed URL and
 * flips response_heard=true (farmer-scoped RPC) so the header badge clears. Never
 * writes a response. All failure paths keep the panel usable.
 */
export const NotificationsPanel = ({ isOpen, onClose }: NotificationsPanelProps) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['help-responses', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('help_messages')
        .select('id, created_at, response_audio_path, response_heard')
        .not('response_audio_path', 'is', null)
        .order('created_at', { ascending: false });
      return (data ?? []) as ResponseRow[];
    },
    enabled: isOpen && !!user?.id,
  });

  const playResponse = async (row: ResponseRow) => {
    setErrorId(null);
    setLoadingId(row.id);
    try {
      const { data, error } = await supabase.storage
        .from(RESPONSE_BUCKET)
        .createSignedUrl(row.response_audio_path, 3600);
      if (error || !data?.signedUrl) throw new Error('sign_failed');

      audioRef.current?.pause();
      const audio = new Audio(data.signedUrl);
      audioRef.current = audio;
      audio.onended = () => setPlayingId((id) => (id === row.id ? null : id));
      audio.onerror = () => { setErrorId(row.id); setPlayingId(null); };
      await audio.play();
      setLoadingId(null);
      setPlayingId(row.id);

      // Mark heard (farmer-scoped RPC, response_heard only). Best-effort — a
      // failure must not break playback or the panel.
      if (!row.response_heard) {
        supabase.rpc('mark_help_response_heard', { p_message_id: row.id })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['help-response-unheard', user?.id] });
            queryClient.invalidateQueries({ queryKey: ['help-responses', user?.id] });
          })
          .catch(() => { /* leave badge as-is; farmer can retry */ });
      }
    } catch {
      setLoadingId(null);
      setPlayingId(null);
      setErrorId(row.id);
    }
  };

  if (!isOpen) return null;

  const fmtDate = (iso: string) => {
    try {
      // FIX-TV-LAO-STRINGS-3: dd/MM/yyyy in every language (short month names were "M10" in Lao/Khmer).
      return formatDayMonth(iso);
    } catch {
      return iso.slice(0, 10);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) { audioRef.current?.pause(); onClose(); } }}
    >
      <div className="w-full max-w-md max-h-[85dvh] overflow-y-auto rounded-2xl bg-background shadow-xl p-5 space-y-4 animate-fade-in">
        <div className="sticky top-0 -mt-1 pt-1 pb-1 bg-background flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">{t('notifications.title', 'Notifications')}</h2>
          <button
            onClick={() => { audioRef.current?.pause(); onClose(); }}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-accent text-muted-foreground"
            aria-label={t('common.close', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Inbox className="w-12 h-12 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t('notifications.empty', 'No responses yet. When our team replies to your voice message, it will appear here.')}
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => (
              <li key={row.id} className="rounded-xl border border-border p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Volume2 className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {t('notifications.response', 'Response from AGXL')}
                  </p>
                  <p className="text-xs text-muted-foreground">{fmtDate(row.created_at)}</p>
                  {errorId === row.id && (
                    <p className="text-xs text-destructive mt-1">
                      {t('notifications.playError', 'Could not play this response. Please try again.')}
                    </p>
                  )}
                </div>
                {!row.response_heard && playingId !== row.id && (
                  <span className="shrink-0 text-[10px] font-bold uppercase text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    {t('notifications.new', 'New')}
                  </span>
                )}
                {row.response_heard && playingId !== row.id && (
                  <CheckCircle2 className="w-4 h-4 text-success shrink-0" aria-label={t('notifications.heard', 'Heard')} />
                )}
                <button
                  type="button"
                  onClick={() => playResponse(row)}
                  disabled={loadingId === row.id}
                  aria-label={t('notifications.play', 'Play')}
                  className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center active:scale-95 disabled:opacity-60"
                >
                  {loadingId === row.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
